import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const isSubscribed = () => {
    if (!profile) return false;
    return (
      profile.subscription_status === 'active' || 
      profile.subscription_status === 'trialing' || 
      profile.plan === 'pro'
    );
  }

  useEffect(() => {
    let mounted = true

    const fetchProfile = async (userId) => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();
        if (mounted) setProfile(data);
      } catch (err) {
        console.error('fetchProfile error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // getSession() is the authoritative source for the initial session.
    // It is not affected by StrictMode subscription teardown.
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        if (session?.user) {
          setUser(session.user);
          fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });

    // onAuthStateChange handles subsequent state changes only.
    // INITIAL_SESSION is skipped — already handled by getSession() above.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'INITIAL_SESSION') return;
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    }
  }, [])

  const value = { user, profile, loading, isSubscribed, signOut: () => supabase.auth.signOut(), supabase }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext);